import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { FinancialInstitution } from '../models/financial-institution';
import { ApiResponse } from '../models/securepay';
import {
  AccountNameEnquiryResult,
  describeAccountEnquiryError,
  toAccountNameEnquiryResult,
  VerifyAccountRequest,
  VerifyAccountResponse,
} from '../models/account-name-enquiry';

@Injectable({
  providedIn: 'root',
})
export class BankService {
  private http = inject(HttpClient);

  getBankList(baseUrl: string) {
    return this.http
      .get<ApiResponse<FinancialInstitution[]>>(
        `${baseUrl}/api/NibssServices/nibss-easypay-get-financial-institution`,
      )
      .pipe(map((response) => response.data ?? []));
  }

  /**
   * Raw envelope. Resolves with `success: false` / `data: null` rather than
   * throwing — the backend answers HTTP 200 for an unresolvable account.
   * Transport failures (4xx/5xx/offline) still error the stream.
   */
  verifyAccount(
    baseUrl: string,
    payload: VerifyAccountRequest,
  ): Observable<ApiResponse<VerifyAccountResponse | null>> {
    return this.http.post<ApiResponse<VerifyAccountResponse | null>>(
      `${baseUrl}/api/v2/subaccounts/verify-account`,
      payload,
    );
  }

  /**
   * Normalised enquiry: **never errors**, always emits exactly one
   * {@link AccountNameEnquiryResult}. This is what form wiring consumes, so
   * that `success: false`, `data: null`, an unrecognised field name and a
   * transport error all land on a single code path.
   */
  resolveAccountName(
    baseUrl: string,
    payload: VerifyAccountRequest,
  ): Observable<AccountNameEnquiryResult> {
    return this.verifyAccount(baseUrl, payload).pipe(
      map((response) => toAccountNameEnquiryResult(response)),
      catchError((error: unknown) => {
        const httpError = error as {
          error?: { message?: string };
          message?: string;
        };
        return of<AccountNameEnquiryResult>({
          resolved: false,
          message: describeAccountEnquiryError(
            httpError?.error?.message ?? httpError?.message,
          ),
        });
      }),
    );
  }
}
